import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { MatMenuTrigger } from '@angular/material/menu';
import { MatCalendar, MatCalendarCellClassFunction } from '@angular/material/datepicker';
import { SelectedRange } from '@app/shared/models/volume-dashboard.model';
import { TranslateService } from '@ngx-translate/core';
import { localizeDate } from '@app/shared/utilities/calendar-date';

interface MonthPreset {
  key: string;
  label: string;
}

@Component({
  selector: 'app-material-month-calendar-picker',
  templateUrl: './material-month-calendar-picker.component.html',
  styleUrl: './material-month-calendar-picker.component.scss',
  standalone: false,
})
export class MaterialMonthCalendarPickerComponent implements OnInit {
  @Input() defaultPreset = 'lastMonth';
  @Output() rangeChange = new EventEmitter<SelectedRange>();
  @ViewChild(MatMenuTrigger) private menuTrigger?: MatMenuTrigger;
  @ViewChild(MatCalendar) private calendar?: MatCalendar<Date>;

  fromMonth: Date | null = null;
  toMonth: Date | null = null;
  activePreset = '';
  private rangeAnchor: Date | null = null;
  private appliedFromMonth: Date | null = null;
  private appliedToMonth: Date | null = null;
  private appliedPreset = '';

  startAt = new Date();
  // Monthly reporting includes only complete months.
  readonly maxDate = this.lastOfMonth(new Date(this.today().getFullYear(), this.today().getMonth() - 1, 1));

  readonly presets: MonthPreset[] = [
    { key: 'lastMonth', label: 'App.Picker.Presets.LastMonth' },
    { key: 'last3', label: 'App.Picker.Presets.Last3Months' },
    { key: 'ytd', label: 'App.Picker.Presets.YearToDate' },
  ];

  constructor(private translate: TranslateService) {}

  ngOnInit(): void {
    const start = this.presets.find(p => p.key === this.defaultPreset) ?? this.presets[0];
    this.applyPreset(start);
    this.commitSelection();
    Promise.resolve().then(() => this.emit());
  }

  beginEdit(): void {
    // Each open starts from the last applied month range.
    this.restoreAppliedSelection();
    this.lockToYearView();
  }

  onMonthSelected(month: Date): void {
    const m = this.firstOfMonth(month);
    // One click is a valid single-month range; a second expands from that anchor.
    if (!this.rangeAnchor) {
      this.rangeAnchor = m;
      this.fromMonth = m;
      this.toMonth = m;
    } else {
      this.fromMonth = m < this.rangeAnchor ? m : this.rangeAnchor;
      this.toMonth = m < this.rangeAnchor ? this.rangeAnchor : m;
      this.rangeAnchor = null;
    }
    this.activePreset = '';
    this.lockToYearView();
  }

  // Material drills into day view after a month selection.
  private lockToYearView(): void {
    Promise.resolve().then(() => {
      if (!this.calendar) return;
      this.calendar.currentView = 'year';
      // Material exposes this refresh hook for dateClass styling.
      this.calendar.updateTodaysDate();
    });
  }

  dateClass: MatCalendarCellClassFunction<Date> = (cell, view) => {
    if (view !== 'year') return '';
    const m = this.firstOfMonth(cell);
    if (this.fromMonth && this.sameMonth(m, this.fromMonth)) return 'mcp-start';
    if (this.toMonth && this.sameMonth(m, this.toMonth)) return 'mcp-end';
    if (this.fromMonth && this.toMonth && m > this.fromMonth && m < this.toMonth) return 'mcp-inrange';
    return '';
  };

  applyPreset(preset: MonthPreset): void {
    const [from, to] = this.rangeFor(preset.key);
    this.fromMonth = from;
    this.toMonth = to;
    this.activePreset = preset.key;
    this.rangeAnchor = null;
    this.startAt = new Date(to);
    this.lockToYearView();
  }

  apply(): void {
    if (this.fromMonth && this.toMonth) {
      this.commitSelection();
      this.emit();
      this.rangeAnchor = null;
      this.menuTrigger?.closeMenu();
    }
  }

  clear(): void {
    this.fromMonth = null;
    this.toMonth = null;
    this.activePreset = '';
    this.rangeAnchor = null;
    this.lockToYearView();
  }

  get appliedPresetLabel(): string {
    return this.presets.find(p => p.key === this.appliedPreset)?.label ?? '';
  }

  get rangeText(): string {
    return this.formatRange(this.fromMonth, this.toMonth);
  }

  get appliedRangeText(): string {
    return this.formatRange(this.appliedFromMonth, this.appliedToMonth);
  }

  private formatRange(from: Date | null, to: Date | null): string {
    if (!from || !to) return '';
    const a = this.monthLabel(from), b = this.monthLabel(to);
    return a === b ? a : `${a} – ${b}`;
  }

  private commitSelection(): void {
    this.appliedFromMonth = this.fromMonth ? new Date(this.fromMonth) : null;
    this.appliedToMonth = this.toMonth ? new Date(this.toMonth) : null;
    this.appliedPreset = this.activePreset;
  }

  private restoreAppliedSelection(): void {
    this.fromMonth = this.appliedFromMonth ? new Date(this.appliedFromMonth) : null;
    this.toMonth = this.appliedToMonth ? new Date(this.appliedToMonth) : null;
    this.activePreset = this.appliedPreset;
    this.rangeAnchor = null;
    if (this.toMonth) this.startAt = new Date(this.toMonth);
  }

  private emit(): void {
    if (!this.fromMonth || !this.toMonth) return;
    this.rangeChange.emit({
      from: this.firstOfMonth(this.fromMonth),
      to: this.lastOfMonth(this.toMonth),
      preset: this.activePreset || 'custom',
      grain: 'month',
    });
  }

  private rangeFor(key: string): [Date, Date] {
    const t = this.today();
    const lastComplete = new Date(t.getFullYear(), t.getMonth() - 1, 1);
    switch (key) {
      case 'last3':
        return [new Date(t.getFullYear(), t.getMonth() - 3, 1), lastComplete];
      case 'ytd': {
        const jan = new Date(t.getFullYear(), 0, 1);
        return lastComplete < jan ? [lastComplete, lastComplete] : [jan, lastComplete];
      }
      case 'lastMonth':
      default:
        return [lastComplete, lastComplete];
    }
  }

  private today(): Date { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); }
  private firstOfMonth(d: Date): Date { return new Date(d.getFullYear(), d.getMonth(), 1); }
  private lastOfMonth(d: Date): Date { return new Date(d.getFullYear(), d.getMonth() + 1, 0); }
  private sameMonth(a: Date, b: Date): boolean { return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth(); }
  private monthLabel(d: Date): string {
    return localizeDate(d, { month: 'short', year: 'numeric' }, this.translate.currentLang);
  }
}
